/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Users, ScrollText, FileText, FileSignature, BookOpen, Calendar,
  Stamp, IdCard, ArrowRight, CheckCircle2, AlertTriangle,
} from 'lucide-react';

import SEO from '@/components/SEO';
import LibraryPageLayout from '@/components/library/LibraryPageLayout';
import LibraryStructuredData from '@/components/library/LibraryStructuredData';
import {
  LibraryHero,
  SectionHeader,
  FeatureCheckItem,
  LibraryFAQ,
  LibraryFinalCta,
  GuideMeta,
  OfficialSources,
  relatedGuides,
} from '@/components/library/sections';
import { guideSeoFor } from '@/data/guideSeo';

const { sources: SOURCES_NOTORIETE, howTo: HOWTO_NOTORIETE } = guideSeoFor('/acte-notoriete');

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const CONTENT_CARDS = [
  {
    Icon: ScrollText,
    title: 'Défunt',
    description: "L'identité complète de la personne décédée : nom, prénom, date et lieu de naissance, date et lieu du décès, régime matrimonial et dernier domicile.",
  },
  {
    Icon: Users,
    title: 'Héritiers',
    description: "La liste des personnes appelées à recueillir la succession : conjoint, enfants, parents, frères et sœurs, neveux ou nièces selon les cas.",
  },
  {
    Icon: IdCard,
    title: 'Degrés de parenté',
    description: "Le lien juridique précis entre chaque héritier et le défunt, conditionnant l'ordre de dévolution, les parts et l'imposition future.",
  },
  {
    Icon: BookOpen,
    title: 'Testament éventuel',
    description: "Si un testament a été déposé chez un notaire ou retrouvé, il est cité et ses dispositions sont prises en compte dans l'acte.",
  },
  {
    Icon: FileText,
    title: "État civil",
    description: "Les pièces d'état civil utilisées par le notaire pour établir la qualité d'héritier : acte de naissance, livret de famille, contrat de mariage.",
  },
  {
    Icon: FileSignature,
    title: 'Déclaration des héritiers',
    description: "Les héritiers déclarent solennellement qu'ils n'ont pas connaissance d'autres héritiers et qu'aucun autre testament ne leur est connu.",
  },
  {
    Icon: Stamp,
    title: 'Références notariales',
    description: "Le nom et l'étude du notaire instrumentaire, le numéro de répertoire de l'acte, ainsi que le timbre fiscal apposé pour son enregistrement.",
  },
  {
    Icon: Calendar,
    title: "Date de l'acte",
    description: "La date à laquelle l'acte a été signé devant le notaire — c'est elle qui fait foi pour toutes les démarches ultérieures.",
  },
];

const POINTS_A_VERIFIER = [
  "Vérifier que tous les héritiers sont mentionnés, y compris ceux d'unions précédentes ou les enfants reconnus tardivement.",
  "Contrôler les degrés de parenté indiqués — une erreur peut entraîner une mauvaise répartition.",
  "S'assurer qu'aucun testament n'a été omis : la présence d'un testament modifie les parts attribuées.",
  "Vérifier l'état civil de chaque héritier : nom, prénoms, dates, adresse actuelle.",
  "Confirmer le régime matrimonial du défunt : communauté, séparation, participation aux acquêts.",
  "Conserver une copie originale : c'est ce document qui débloque les comptes bancaires et l'immobilier.",
];

const AUTO_CHECKS = [
  "Héritiers identifiés et leurs qualités",
  "Liens familiaux entre le défunt et chaque héritier",
  "Testament éventuel et ses dispositions",
  "Informations importantes consignées dans l'acte",
  "Anomalies ou points à signaler",
  "Conséquences pratiques pour vous",
  "Prochaines démarches à effectuer",
  "Questions à poser à votre notaire",
];

const FAQ_ITEMS = [
  {
    q: "Qu'est-ce qu'un acte de notoriété ?",
    a: "L'acte de notoriété est un acte authentique établi par un notaire qui identifie officiellement les héritiers d'une personne décédée. Il indique le défunt, ses ayants droit, leurs liens de parenté, ainsi que les dispositions testamentaires éventuelles. C'est souvent le tout premier document signé après un décès — celui qui permet de débloquer les démarches qui suivent.",
  },
  {
    q: "Est-il obligatoire ?",
    a: "Il n'est pas obligatoire dans tous les cas. Lorsque la succession est de faible montant, certaines banques acceptent un simple « certificat d'hérédité » délivré gratuitement en mairie. Mais dès qu'un bien immobilier est en jeu, qu'un testament existe, qu'il y a une situation familiale complexe ou un montant supérieur à 5 000 €, l'acte de notoriété devient en pratique indispensable.",
  },
  {
    q: "Qui le signe ?",
    a: "Il est signé devant le notaire par les héritiers eux-mêmes, ou par l'un d'entre eux mandaté par les autres. Tous les héritiers présumés sont en principe convoqués ou cités. Le notaire, en tant qu'officier public, authentifie le document et lui confère sa valeur juridique.",
  },
  {
    q: "Combien coûte-t-il ?",
    a: "Le tarif est encadré par décret. Comptez généralement entre 60 et 100 € HT d'émoluments pour l'acte lui-même, auxquels s'ajoutent les recherches préalables (notamment auprès du Fichier central des dispositions de dernières volontés), les frais de dossier et la TVA. Le coût final tourne souvent autour de 200 à 300 € selon la complexité de la situation familiale.",
  },
  {
    q: "Que faire après l'avoir reçu ?",
    a: "Avec l'acte de notoriété, vous pouvez débloquer les comptes bancaires du défunt, demander le versement d'une assurance-vie, accéder aux coffres-forts, recevoir le capital décès et engager toutes les démarches administratives au nom de la succession. Conservez précieusement plusieurs copies — vous en aurez besoin pendant des mois.",
  },
  {
    q: "Comment HÉRITA l'analyse-t-il ?",
    a: "HÉRITA lit l'acte intégralement, identifie chaque héritier mentionné, son lien de parenté avec le défunt, la présence ou non d'un testament, les références de l'étude notariale et tous les éléments clés. Vous recevez en quelques minutes une lecture claire, structurée et personnalisée, avec les points à vérifier et les démarches à prévoir.",
  },
  {
    q: "Quelle est la différence avec un certificat d'hérédité ?",
    a: "Le certificat d'hérédité est un document gratuit délivré par certaines mairies pour les successions de faible montant, sans bien immobilier ni testament. L'acte de notoriété, lui, est authentique, signé par un notaire et indispensable dès que la succession comprend des biens importants. Sa portée juridique est bien supérieure et il est exigé par toutes les administrations.",
  },
  {
    q: "Combien de temps faut-il pour l'obtenir ?",
    a: "Le notaire peut établir l'acte de notoriété en quelques jours dès que toutes les pièces d'état civil ont été rassemblées (acte de décès, livret de famille, contrats de mariage). Le délai peut s'étirer si une recherche au Fichier central des dispositions de dernières volontés est nécessaire ou si des héritiers résident à l'étranger.",
  },
  {
    q: "Peut-on en obtenir une copie ?",
    a: "Oui. Vous pouvez demander à votre notaire une copie authentique ou plusieurs copies exécutoires de l'acte. Vous en aurez besoin pour chaque démarche : banque, assurance, administration fiscale, vente d'un bien immobilier. Conservez l'original en lieu sûr.",
  },
  {
    q: "Quel rapport avec la déclaration de succession ?",
    a: "Ce sont deux documents complémentaires mais distincts. L'acte de notoriété établit la qualité d'héritier — qui hérite. La déclaration de succession, déposée plus tard à l'administration fiscale, détaille ce qui est hérité — biens, dettes, valeurs — et calcule les droits dus. Le premier précède toujours la seconde dans le règlement d'une succession.",
  },
];

const BREADCRUMB = [
  { label: 'Accueil', to: '/' },
  { label: 'Guides', to: '/comprendre-document-notarie' },
  { label: 'Acte de notoriété' },
];

const SEO_TITLE = 'Acte de notoriété : tout comprendre simplement | HÉRITA';
const SEO_DESCRIPTION =
  "Comprenez facilement un acte de notoriété. Découvrez son rôle, les informations qu'il contient, à quoi il sert dans une succession et analysez votre propre document grâce à HÉRITA.";

const ActeNotoriete = () => {
  return (
    <>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        path="/acte-notoriete"
        ogTitle="Acte de notoriété — Guides HÉRITA"
        ogDescription={SEO_DESCRIPTION}
      />
      <LibraryStructuredData
        path="/acte-notoriete"
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        breadcrumb={BREADCRUMB}
        faq={FAQ_ITEMS}
        howTo={HOWTO_NOTORIETE}
        datePublished="2026-02-18"
        dateModified="2026-02-18"
      />

      <LibraryPageLayout
        breadcrumb={BREADCRUMB}
        testId="page-acte-notoriete"
      >
        {/* ── HERO ── */}
        <LibraryHero
          eyebrow="Guides HÉRITA"
          title="Comprendre un acte de notoriété"
          subtitle="L'acte de notoriété est généralement le premier document établi par le notaire après un décès. Il identifie officiellement les héritiers et constitue une étape essentielle du règlement de la succession."
          trustItems={[
            'Analyse gratuite au départ',
            'Aucun compte requis',
            'Documents supprimés après analyse',
          ]}
        />

        {/* META — reading time, last update, table of contents */}
        <GuideMeta
          readingTime="8 min"
          lastUpdated="18 février 2026"
          sections={[
            { id: 'definition', label: 'Définition' },
            { id: 'utilite', label: 'Utilité' },
            { id: 'contenu', label: 'Contenu' },
            { id: 'vigilance', label: 'Vigilance' },
            { id: 'analyse', label: 'Analyse HÉRITA' },
            { id: 'exemple', label: 'Exemple' },
            { id: 'faq', label: 'FAQ' },
          ]}
        />

        {/* ── DÉFINITION ── */}
        <section
          id="definition"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="def-heading"
        >
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              eyebrow="Définition"
              title={"Qu'est-ce qu'un acte de notoriété\u00A0?"}
            />
            <h2 id="def-heading" className="sr-only">
              Définition d'un acte de notoriété
            </h2>

            <div className="font-['Work_Sans'] text-[15.5px] sm:text-[16px] text-[#2C363F] leading-[1.85] space-y-6">
              <p>
                Quand une personne décède, beaucoup de choses doivent reprendre
                leur cours en son nom&nbsp;: les comptes bancaires, l'assurance
                vie, le versement éventuel d'une retraite ou d'un capital, la
                gestion d'un bien immobilier. Avant de pouvoir agir, encore
                faut-il prouver, document à l'appui,{' '}
                <strong className="font-semibold text-[#1A2530]">qui est légalement habilité à le faire</strong>.
                C'est exactement ce que fait l'acte de notoriété.
              </p>

              <p>
                Concrètement, c'est un <em>acte authentique</em>, rédigé par un
                notaire, qui désigne nommément les héritiers d'une personne
                décédée et indique leur lien de parenté avec elle. Il précise
                aussi si un testament a été retrouvé, et dans ce cas, qui sont
                les bénéficiaires des dispositions prises par le défunt de son
                vivant.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Pourquoi un notaire&nbsp;?</strong>{' '}
                Parce que sa fonction d'officier public lui permet d'attester
                des faits que les administrations, les banques ou les
                assurances accepteront sans discussion. L'acte de notoriété
                n'est pas simplement une lettre qui dit «&nbsp;voilà les
                héritiers&nbsp;»&nbsp;: c'est un document doté d'une force
                particulière, opposable à tous.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Un document préparé en amont, avec votre aide.</strong>{' '}
                Pour l'établir, le notaire vous demande des pièces d'état
                civil&nbsp;: acte de décès, livret de famille, contrat de
                mariage du défunt, parfois actes de naissance des héritiers. Il
                interroge également le Fichier central des dispositions de
                dernières volontés pour vérifier qu'aucun testament n'a été
                déposé sans que la famille en soit informée.
              </p>

              <p>
                Une fois ces vérifications faites, le notaire vous reçoit, à
                vous et aux autres héritiers présumés, pour signer l'acte. La
                signature vaut <em>déclaration solennelle</em>&nbsp;: chacun
                affirme qu'à sa connaissance, il n'existe pas d'autre héritier
                que ceux mentionnés. Si plus tard un héritier inconnu était
                retrouvé, l'acte pourrait être complété.
              </p>

              <p>
                Au final, l'acte de notoriété est un point d'ancrage. Tant
                qu'il n'existe pas, beaucoup de portes restent fermées&nbsp;: la
                banque garde les comptes bloqués, l'assurance-vie attend, et
                aucune attestation immobilière ne peut être signée. C'est
                précisément pour cela qu'il est, dans la plupart des
                successions, le premier acte que vous recevez.
              </p>
            </div>
          </div>
        </section>

        {/* ── UTILITÉ ── */}
        <section
          id="utilite"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          aria-labelledby="use-heading"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Utilité"
              title={"À quoi sert-il\u00A0?"}
            />
            <h2 id="use-heading" className="sr-only">À quoi sert un acte de notoriété</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  title: 'Identifier officiellement les héritiers',
                  description: "L'acte établit, noir sur blanc et de manière incontestable, qui hérite et dans quelle proportion. C'est la première vérité juridique de la succession.",
                },
                {
                  title: 'Prouver leur qualité d\'ayant droit',
                  description: "Chaque héritier peut présenter le document à toute administration, banque ou assurance pour faire reconnaître son droit à hériter.",
                },
                {
                  title: 'Débloquer les démarches',
                  description: "Sans cet acte, comptes, contrats, coffres-forts et capitaux restent gelés. C'est la clé qui remet en mouvement le patrimoine du défunt.",
                },
                {
                  title: 'Permettre la suite du règlement',
                  description: "Sur lui s'appuient l'attestation immobilière, la déclaration de succession et le partage. Tout repose, juridiquement, sur ses conclusions.",
                },
              ].map(({ title, description }) => (
                <article
                  key={title}
                  className="bg-white border border-[#E2DBCF] rounded-2xl p-7"
                >
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[24px] text-[#1A2530] mb-3 leading-tight">
                    {title}
                  </h3>
                  <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                    {description}
                  </p>
                </article>
              ))}
            </div>

            {/* Contextual link to Déclaration de succession */}
            <p className="mt-10 font-['Work_Sans'] text-[14px] text-[#2C363F] text-center max-w-2xl mx-auto leading-relaxed">
              Une fois les héritiers identifiés, vient l'étape suivante&nbsp;:
              la <Link to="/declaration-succession" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">déclaration de succession</Link>,
              qui détaille les biens, les dettes et les droits à payer.
            </p>
          </div>
        </section>

        {/* ── CONTENU ── */}
        <section
          id="contenu"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="content-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Contenu"
              title={"Que contient un acte de notoriété\u00A0?"}
              intro="Huit éléments principaux composent l'acte. Ensemble, ils dressent la photographie officielle de la famille au moment du décès."
            />
            <h2 id="content-heading" className="sr-only">Contenu d'un acte de notoriété</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {CONTENT_CARDS.map(({ Icon, title, description }) => (
                <article
                  key={title}
                  className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-6 hover:shadow-md hover:border-[#D3CCBE] transition-all"
                >
                  <div className="w-11 h-11 rounded-full bg-[#E3D5CA] flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#1A2530]" aria-hidden="true" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[20px] sm:text-[22px] text-[#1A2530] mb-2 leading-tight">
                    {title}
                  </h3>
                  <p className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] leading-relaxed">
                    {description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── POINTS À VÉRIFIER ── */}
        <section
          id="vigilance"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          aria-labelledby="check-heading"
        >
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              eyebrow="Vigilance"
              title="Les points importants à vérifier"
              intro="Un acte de notoriété engage toutes les démarches qui suivront. Voici ce qu'il faut relire attentivement avant de signer."
            />
            <h2 id="check-heading" className="sr-only">Points à vérifier sur un acte de notoriété</h2>

            <ul className="space-y-3 list-none p-0">
              {POINTS_A_VERIFIER.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-white border border-[#E2DBCF] rounded-xl p-5"
                >
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-3.5 w-3.5 text-[#1A2530]" aria-hidden="true" />
                  </span>
                  <span className="font-['Work_Sans'] text-[14.5px] text-[#1A2530] leading-snug">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── AUTO CHECKS ── */}
        <section
          id="analyse"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="auto-heading"
        >
          <div className="max-w-5xl mx-auto">
            <SectionHeader
              eyebrow="Sans rien demander"
              title="Ce que HÉRITA identifie automatiquement"
              intro="Dès l'import de l'acte, HÉRITA repère les héritiers, les liens familiaux et les éléments qui demandent votre attention."
            />
            <h2 id="auto-heading" className="sr-only">Ce que HÉRITA identifie</h2>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0">
              {AUTO_CHECKS.map((label) => (
                <FeatureCheckItem key={label} label={label} />
              ))}
            </ul>
          </div>
        </section>

        {/* ── EXEMPLE ── */}
        <section
          id="exemple"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          aria-labelledby="example-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Avant · Après"
              title="Un exemple simplifié"
              intro="À gauche, un extrait fictif d'acte de notoriété. À droite, ce qu'HÉRITA en aurait expliqué."
            />
            <h2 id="example-heading" className="sr-only">Exemple avant et après</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
              <article className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-7 sm:p-9 font-['Cormorant_Garamond'] text-[#1A2530]">
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-4">
                  Extrait du document (fictif)
                </p>
                <p className="text-[16px] sm:text-[17px] leading-[1.85] italic">
                  «&nbsp;L'an deux mille vingt-quatre, le quinze avril, par
                  devant Maître Hélène ROCHE, notaire à Bordeaux, ont comparu
                  Madame Léa DURAND, veuve, demeurant&nbsp;[…], et Mademoiselle
                  Camille DURAND, célibataire, demeurant&nbsp;[…], lesquelles
                  ont déclaré que Monsieur Pierre DURAND, leur époux et père,
                  est décédé à Paris le quatorze mars deux mille vingt-quatre,
                  laissant pour lui succéder, à défaut de testament déposé,
                  son épouse survivante (commune en biens, régime légal de
                  communauté réduite aux acquêts) et sa fille unique, seuls
                  héritiers connus, à l'exclusion de toute autre personne.&nbsp;»
                </p>
              </article>

              <article className="bg-white border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF] rounded-2xl p-7 sm:p-9 shadow-md">
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#1A2530] mb-4">
                  Lecture HÉRITA
                </p>
                <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] mb-5 leading-tight">
                  Ce que cela signifie pour Camille
                </h3>

                <ul className="space-y-3.5 list-none p-0">
                  {[
                    { strong: 'Le défunt est Pierre DURAND', rest: ', décédé le 14 mars 2024 à Paris.' },
                    { strong: 'Deux héritières sont identifiées', rest: ' : sa veuve, Léa DURAND, et sa fille unique, Camille DURAND.' },
                    { strong: 'Aucun testament n\'a été déposé', rest: ' au Fichier central des dispositions de dernières volontés.' },
                    { strong: 'Le régime matrimonial', rest: ' était la communauté réduite aux acquêts — ce qui détermine la part recueillie par chacune.' },
                    { strong: 'Avec cet acte', rest: ', les banques, assurances et notaires peuvent désormais traiter la succession.' },
                  ].map((line, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1A2530] flex-shrink-0" />
                      <span className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#1A2530] leading-snug">
                        <strong className="font-semibold">{line.strong}</strong>{line.rest}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-6 pt-5 border-t border-[#E2DBCF] font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                  Lecture réalisée en moins de 30 secondes
                </p>
              </article>
            </div>

            <p className="mt-8 font-['Work_Sans'] text-[12.5px] text-[#2C363F] italic text-center opacity-80">
              Exemple illustratif. Noms, dates et études notariales sont
              inventés et ne correspondent à aucun dossier réel.
            </p>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section
          id="faq"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="faq-heading"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Questions fréquentes"
              title="Tout ce que vous voulez savoir"
              intro="Dix questions concrètes pour comprendre l'acte de notoriété sans détour."
            />
            <h2 id="faq-heading" className="sr-only">FAQ acte de notoriété</h2>

            <LibraryFAQ items={FAQ_ITEMS} defaultOpen={0} />
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <LibraryFinalCta
          title={"Vous avez reçu un acte de notoriété\u00A0?"}
          subtitle="Déposez votre document. HÉRITA identifie automatiquement les héritiers, les informations importantes et les prochaines étapes afin de vous fournir une explication claire et personnalisée."
        />

        {/* ── OFFICIAL SOURCES ── */}
        <OfficialSources sources={SOURCES_NOTORIETE} />

        {/* ── DÉCOUVRIR AUSSI ── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-t border-[#E2DBCF]"
          aria-labelledby="more-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Aller plus loin"
              title="Guides associés"
              intro="Les autres guides HÉRITA pour comprendre les actes notariés qui peuvent croiser votre succession."
            />
            <h2 id="more-heading" className="sr-only">Découvrir aussi</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {relatedGuides('/acte-notoriete').map(({ label, to }) => (
                <Link
                  key={to}
                  to={to}
                  className="group block bg-[#F9F7F3] border border-[#E2DBCF] rounded-xl p-5 hover:border-[#1A2530] hover:bg-white transition-all"
                  data-testid={`more-link-${to}`}
                >
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 mb-2">
                    Comprendre
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-[19px] sm:text-[21px] text-[#1A2530] leading-tight mb-3 font-medium">
                    {label}
                  </p>
                  <span className="inline-flex items-center font-['Work_Sans'] text-[12.5px] font-medium text-[#1A2530] group-hover:underline underline-offset-[5px] decoration-[#1A2530]">
                    Lire le guide
                    <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </LibraryPageLayout>
    </>
  );
};

export default ActeNotoriete;
